import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { movies } from '@/mocks/movies';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);
  const categoryName = useMemo(() => {
    if (!movie) return '';
    return categories.find((c) => c.slug === movie.category)?.name || '';
  }, [movie]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-800 mb-2">影片未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该影片可能已下架或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.id !== movie.id && (m.category === movie.category || m.tags.some((t) => movie.tags.includes(t))))
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${movie.title} - 小草影院免费在线观看`,
    description: movie.description,
    datePublished: `${movie.year}-01-01`,
    author: {
      '@type': 'Organization',
      name: '小草影院',
    },
    publisher: {
      '@type': 'Organization',
      name: '小草影院',
      url: 'https://xiaocaoyingyuan.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://xiaocaoyingyuan.com/article/${movie.id}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { name: categoryName, path: `/category/${movie.category}` },
                { name: movie.title },
              ]}
            />

            <article className="px-4 md:px-6 py-6">
              <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                <div className="lg:w-[300px] flex-shrink-0">
                  <div className="sticky top-20">
                    <div className="aspect-[3/4] rounded-2xl overflow-hidden">
                      <img
                        src={movie.coverImage}
                        alt={movie.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                    {movie.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="px-3 py-1 text-sm font-semibold bg-accent-500 text-white rounded-full">
                      {movie.rating} 分
                    </span>
                    <span className="text-sm text-foreground-500">{movie.year}</span>
                    <span className="text-sm text-foreground-500">{movie.updateInfo}</span>
                    <span className="px-2 py-0.5 text-xs bg-background-100 text-foreground-600 rounded-md">
                      {categoryName}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="space-y-3 text-sm text-foreground-600 mb-6">
                    <div className="flex">
                      <span className="w-16 flex-shrink-0 text-foreground-400">导演：</span>
                      <span>{movie.director || '暂无信息'}</span>
                    </div>
                    <div className="flex">
                      <span className="w-16 flex-shrink-0 text-foreground-400">主演：</span>
                      <span>{movie.actors.join(' / ')}</span>
                    </div>
                    <div className="flex">
                      <span className="w-16 flex-shrink-0 text-foreground-400">地区：</span>
                      <span>
                        {movie.region === 'china'
                          ? '中国大陆'
                          : movie.region === 'hongkong'
                          ? '中国香港'
                          : movie.region === 'korea'
                          ? '韩国'
                          : movie.region === 'japan'
                          ? '日本'
                          : movie.region === 'america'
                          ? '美国'
                          : movie.region === 'europe'
                          ? '欧洲'
                          : '其他'}
                      </span>
                    </div>
                  </div>

                  <Link
                    to={`/category/${movie.category}`}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer mb-6"
                  >
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即播放
                  </Link>

                  <div className="prose prose-sm max-w-none mt-6">
                    <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">剧情简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">{movie.longDescription}</p>
                  </div>
                </div>
              </div>
            </article>

            {relatedMovies.length > 0 && (
              <section className="px-4 md:px-6 py-8 border-t border-background-200/70">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                  {relatedMovies.map((rm) => (
                    <Link
                      key={rm.id}
                      to={`/article/${rm.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-2">
                        <img
                          src={rm.coverImage}
                          alt={rm.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-0.5 text-xs font-semibold bg-accent-500 text-white rounded-md">
                            {rm.rating}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-xs font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {rm.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}